import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  // Memo dell'account_type precedente per detect cambio remoto da admin.
  const prevAccountTypeRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem("ah_token")) {
      setUser(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      // Se l'admin ha cambiato il tipo account dell'utente da remoto, lo notifichiamo
      // e forziamo un reload completo della pagina per garantire che TUTTE le route
      // condizionali (sidebar, dashboard, prezzi, navigation) usino il nuovo tipo.
      const prev = prevAccountTypeRef.current;
      const next = data?.account_type || null;
      if (prev && next && prev !== next) {
        const labels = { studio: "Studio Architetto", trader: "Trader / Rivenditore", real_estate: "Agenzia Immobiliare" };
        toast.info(`Il tuo tipo account è stato aggiornato in "${labels[next] || next}". Ricarico la pagina…`, { duration: 4000 });
        setTimeout(() => window.location.reload(), 1200);
      }
      prevAccountTypeRef.current = next;
      setUser(data);
      return data;
    } catch (err) {
      console.error("[auth] refresh failed", err);
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refresh();
      setChecking(false);
    })();
  }, [refresh]);

  // Polling silenzioso ogni 60 secondi: rileva cambi remoti come account_type
  // (riqualificazione da admin), nuovi crediti wallet, ban, ecc. Senza polling
  // un utente loggato che viene riqualificato continuerebbe a vedere la vecchia UI
  // finché non rilogga.
  useEffect(() => {
    if (!user || user === false) return undefined;
    const interval = setInterval(() => {
      refresh().catch(() => { /* silent */ });
    }, 60_000);
    return () => clearInterval(interval);
  }, [user, refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    prevAccountTypeRef.current = data?.account_type || null;
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    prevAccountTypeRef.current = data?.account_type || null;
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.error("[auth] logout failed", err); }
    localStorage.removeItem("ah_token");
    prevAccountTypeRef.current = null;
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, checking, login, register, logout, refresh, formatApiErrorDetail }),
    [user, checking, login, register, logout, refresh]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
